import { DateTime } from "luxon";
import { useHistory } from "react-router-dom";
import type { GetQuotationrequestRSDef } from "../../../api/quotation/types";
import { brand } from "../../../context";
import { useApoAuth } from "../../../hooks";
import { generateQuotationChatRoomCode } from "./api";
import { StatusTag, ViewOfferButton, ViewQuotationChatButton } from "./components";

export type QuotationRequestDesktopTableProps = {
  quotations: GetQuotationrequestRSDef[];
  hasChatRoomMapping: Record<string, boolean>;
  unreadCountsByQuotationReference: Record<string, number>;
};

export function QuotationRequestDesktopTable({
  quotations,
  hasChatRoomMapping,
  unreadCountsByQuotationReference,
}: QuotationRequestDesktopTableProps) {
  return (
    <table className="Table Quotations__body__desktoptable" data-brand={brand}>
      <QuotationTableHeader />
      <tbody>
        {quotations.map((q, idx) => (
          <QuotationTableRow
            key={`${idx}-${q.reference}`}
            quotation={q}
            index={idx}
            hasChatRoomMapping={hasChatRoomMapping}
            unreadCount={unreadCountsByQuotationReference[q.reference] || 0}
          />
        ))}
      </tbody>
    </table>
  );
}

export function QuotationTableHeader() {
  return (
    <thead>
      <tr>
        <th className="text-left! w-[3rem]">#</th>
        <th className="text-left!">Reference</th>
        <th className="text-left!">Title</th>
        <th className="text-left! w-[11rem]">Trip Start Date</th>
        <th className="text-left!">Created</th>
        <th className="text-left!">Due Date</th>
        <th className="text-left! w-[8rem]">Status</th>
        <th className="text-left! w-[6rem]">Actions</th>
      </tr>
    </thead>
  );
}

export type QuotationTableRowProps = {
  quotation: GetQuotationrequestRSDef;
  index: number;
  hasChatRoomMapping: Record<string, boolean>;
  unreadCount: number;
};

export function QuotationTableRow({
  quotation,
  index,
  hasChatRoomMapping,
  unreadCount,
}: QuotationTableRowProps) {
  const history = useHistory();
  const { user } = useApoAuth();
  const userEmail = user?.email;

  return (
    <tr className="odd:bg-gray-200">
      <td className="text-left! w-[3rem] text-xs!">{index + 1}</td>
      <td className="text-left! text-xs!">{quotation?.reference || "N/A"}</td>
      <td className="text-left! text-xs!">{quotation?.title || "N/A"}</td>
      <td className="text-left! w-[11rem] text-xs!">
        {quotation.trip_start_date
          ? DateTime.fromISO(quotation.trip_start_date).toLocaleString(DateTime.DATE_FULL)
          : "N/A"}
      </td>
      <td className="text-left! text-xs!">
        {quotation.created
          ? DateTime.fromISO(quotation.created).toLocaleString(DateTime.DATETIME_FULL)
          : "N/A"}
      </td>
      <td className="text-left! text-xs!">
        {quotation.due_date
          ? DateTime.fromISO(quotation.due_date).toLocaleString(DateTime.DATETIME_FULL)
          : "N/A"}
      </td>
      <td className="text-left! w-[8rem] text-xs!">
        <StatusTag
          offerStatus={quotation.offer_status}
          status={quotation.status}
          statusDisplay={quotation.status_display}
        />
      </td>
      <td className="text-left! w-[6rem] text-xs!">
        <div className="grid auto-cols-max auto-rows-max gap-1">
          {quotation.chat_session_uid && (
            <ViewQuotationChatButton chatSessionUid={quotation.chat_session_uid} />
          )}
          {hasChatRoomMapping[quotation.reference] && userEmail === "zmj2437@163.com" && (
            <button
              type="button"
              className="Button text-xs! p-1! bg-amber-500! border-amber-600! relative"
              data-brand={brand}
              onClick={() => {
                history.push(
                  `/quotation-chat?room_code=${generateQuotationChatRoomCode(quotation.reference)}&reference=${quotation.reference}`,
                );
              }}
            >
              Chat
              {unreadCount > 0 && (
                <span className="absolute -right-2 -top-1 bg-red-500 text-white rounded-full min-w-[18px] h-[18px] flex items-center justify-center text-[11px] font-bold px-1 leading-none shadow-md">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
          )}
          {!!quotation?.offer_sent_date && quotation.offer_reference && (
            <ViewOfferButton offerReference={quotation.offer_reference} />
          )}
        </div>
      </td>
    </tr>
  );
}
